import { z } from "zod";
import {
  commandContextSchema,
  commandError,
  stableFingerprint,
  type AuditEntry,
  type CommandContext,
  type CommandReceipt,
  type CommandResult,
  type CommandSuccess,
} from "../../lib/command-contract";
import type { ActivityEvent, Lead } from "../../lib/types";
import {
  isAllowedMeetingLink,
  MAX_MEETING_MINUTES,
  MIN_MEETING_MINUTES,
  type Meeting,
} from "./contracts";

/**
 * Comandos de reunião.
 *
 * O comando que importa é `hold`: ele confirma a realização, registra o evento
 * `meeting_completed` e é o único caminho pelo qual uma reunião vira interação
 * real. Os demais organizam a agenda sem tocar em `last_interaction_at`.
 *
 * Reunião realizada é fato consumado: não é remarcada, não é cancelada e não é
 * editada. Corrigir um engano é assunto de "desfazer operação" (§7.4), não de
 * sobrescrever o passado por uma tela de edição.
 */

export interface MeetingCommandState {
  meetings: Meeting[];
  events: ActivityEvent[];
  commandReceipts: CommandReceipt[];
  auditLog: AuditEntry[];
  /**
   * Os leads entram no estado, e não só os ids, porque confirmar a realização
   * grava `lastInteractionAt` no lead na mesma operação que cria o evento — as
   * duas gravações precisam voltar juntas ou nenhuma volta (§7.7).
   */
  leads: Lead[];
  membershipIds?: readonly string[];
}

export interface MeetingCommandValue {
  meeting: Meeting;
  /** Instante gravado em `lastInteractionAt`, quando a reunião foi confirmada. */
  interactionAt?: string;
}

export interface MeetingCommandDeps {
  now: () => string;
  uuid: () => string;
}

export interface MeetingCommandExecution {
  result: CommandResult<MeetingCommandValue>;
  state: MeetingCommandState;
}

const INVALID_CONTEXT: CommandContext = Object.freeze({
  ownerId: "invalid-context",
  actorType: "system",
  actorId: "meeting-command",
  origin: "system",
  commandId: "invalid-context",
  correlationId: "invalid-context",
  idempotencyKey: "invalid-context",
  requestedAt: "1970-01-01T00:00:00.000Z",
  schemaVersion: 1,
});

const instantSchema = z
  .string()
  .trim()
  .min(1, "Informe a data e a hora.")
  .refine((value) => !Number.isNaN(Date.parse(value)), "Data ou hora inválida.");

const participantsSchema = z
  .array(z.string().trim().min(1).max(160))
  .max(30, "Reunião com participantes demais.")
  .default([]);

const linkSchema = z
  .string()
  .trim()
  .max(500)
  .refine(isAllowedMeetingLink, "Informe um endereço http ou https.")
  .optional();

export const scheduleMeetingSchema = z.object({
  leadId: z.string().trim().min(1),
  membershipId: z.string().trim().min(1).optional(),
  title: z.string().trim().min(1, "Informe o objetivo da reunião.").max(160),
  startsAt: instantSchema,
  durationMinutes: z
    .number()
    .int()
    .min(MIN_MEETING_MINUTES, "Duração muito curta.")
    .max(MAX_MEETING_MINUTES, "Duração muito longa.")
    .default(60),
  participants: participantsSchema,
  notes: z.string().trim().max(2000).optional(),
  link: linkSchema,
});

export const rescheduleMeetingSchema = z.object({
  meetingId: z.string().trim().min(1),
  startsAt: instantSchema,
  durationMinutes: z
    .number()
    .int()
    .min(MIN_MEETING_MINUTES)
    .max(MAX_MEETING_MINUTES)
    .optional(),
  expectedVersion: z.number().int().positive().optional(),
});

export const updateMeetingSchema = z.object({
  meetingId: z.string().trim().min(1),
  title: z.string().trim().min(1).max(160).optional(),
  participants: participantsSchema.optional(),
  notes: z.string().trim().max(2000).optional(),
  link: linkSchema,
  expectedVersion: z.number().int().positive().optional(),
});

export const holdMeetingSchema = z.object({
  meetingId: z.string().trim().min(1),
  /** Momento em que a reunião aconteceu; nunca no futuro. */
  heldAt: instantSchema.optional(),
  notes: z.string().trim().max(2000).optional(),
  expectedVersion: z.number().int().positive().optional(),
});

export const cancelMeetingSchema = z.object({
  meetingId: z.string().trim().min(1),
  reason: z.string().trim().min(1, "Informe o motivo do cancelamento.").max(300),
  expectedVersion: z.number().int().positive().optional(),
});

export type ScheduleMeetingInput = z.input<typeof scheduleMeetingSchema>;
export type RescheduleMeetingInput = z.input<typeof rescheduleMeetingSchema>;
export type UpdateMeetingInput = z.input<typeof updateMeetingSchema>;
export type HoldMeetingInput = z.input<typeof holdMeetingSchema>;
export type CancelMeetingInput = z.input<typeof cancelMeetingSchema>;

function cloneState(state: MeetingCommandState): MeetingCommandState {
  return {
    meetings: state.meetings.map((item) => ({ ...item })),
    events: [...state.events],
    commandReceipts: [...state.commandReceipts],
    auditLog: [...state.auditLog],
    leads: [...state.leads],
    membershipIds: state.membershipIds,
  };
}

function checkedContext(context: unknown): CommandContext | undefined {
  const parsed = commandContextSchema.safeParse(context);
  return parsed.success ? parsed.data : undefined;
}

function fail(
  state: MeetingCommandState,
  context: CommandContext,
  code: Parameters<typeof commandError>[1],
  message: string,
): MeetingCommandExecution {
  return {
    state,
    result: commandError(context, code, message, {
      severity: code === "persistence_error" ? "error" : "warning",
    }),
  };
}

function replayLookup(
  state: MeetingCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
): MeetingCommandExecution | null {
  const receipt = state.commandReceipts.find(
    (item) =>
      item.ownerId === context.ownerId &&
      item.command === command &&
      item.idempotencyKey === context.idempotencyKey,
  );
  if (!receipt) return null;
  if (receipt.requestFingerprint !== fingerprint)
    return fail(
      state,
      context,
      "idempotency_conflict",
      "A chave de idempotência já foi usada para outra intenção.",
    );
  if (!receipt.result.ok)
    return { state, result: receipt.result as CommandResult<MeetingCommandValue> };
  return {
    state,
    result: { ...(receipt.result as CommandSuccess<MeetingCommandValue>), status: "replayed" },
  };
}

interface FinishInput {
  state: MeetingCommandState;
  context: CommandContext;
  command: string;
  fingerprint: string;
  value: MeetingCommandValue;
  nextState: MeetingCommandState;
  eventIds?: string[];
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  deps: MeetingCommandDeps;
}

function finish(input: FinishInput): MeetingCommandExecution {
  const occurredAt = input.deps.now();
  const auditId = input.deps.uuid();
  const result: CommandSuccess<MeetingCommandValue> = {
    ok: true,
    status: "applied",
    value: input.value,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    version: input.value.meeting.version,
    eventIds: input.eventIds ?? [],
    outboxJobIds: [],
    auditId,
    warnings: [],
  };
  const audit: AuditEntry = {
    id: auditId,
    ownerId: input.context.ownerId,
    command: input.command,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    causationId: input.context.causationId,
    actorType: input.context.actorType,
    actorId: input.context.actorId,
    origin: input.context.origin,
    targetType: "meeting",
    targetId: input.value.meeting.id,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    result: "applied",
    occurredAt,
    before: input.before,
    after: input.after,
  };
  const receipt: CommandReceipt = {
    id: input.deps.uuid(),
    ownerId: input.context.ownerId,
    command: input.command,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    result,
    createdAt: occurredAt,
    schemaVersion: input.context.schemaVersion,
  };
  return {
    result,
    state: {
      ...input.nextState,
      auditLog: [...input.nextState.auditLog, audit],
      commandReceipts: [...input.nextState.commandReceipts, receipt],
    },
  };
}

function validationMessage(error: unknown): string {
  if (error instanceof z.ZodError) return error.issues[0]?.message ?? "Dados inválidos.";
  return error instanceof Error ? error.message : "Dados inválidos.";
}

/** Reunião aberta: existe e ainda não virou fato nem foi cancelada. */
function openMeeting(
  state: MeetingCommandState,
  meetingId: string,
): { meeting: Meeting } | { error: "not_found" | "held" | "cancelled" } {
  const meeting = state.meetings.find((item) => item.id === meetingId);
  if (!meeting) return { error: "not_found" };
  if (meeting.heldAt) return { error: "held" };
  if (meeting.cancelledAt) return { error: "cancelled" };
  return { meeting };
}

function refuseClosed(
  state: MeetingCommandState,
  context: CommandContext,
  error: "not_found" | "held" | "cancelled",
  verb: string,
): MeetingCommandExecution {
  if (error === "not_found") return fail(state, context, "not_found", "Reunião não encontrada.");
  return fail(
    state,
    context,
    "invalid_state",
    error === "held"
      ? `Reunião já realizada não pode ser ${verb}.`
      : `Reunião cancelada não pode ser ${verb}.`,
  );
}

export function executeScheduleMeeting(
  stateInput: MeetingCommandState,
  input: ScheduleMeetingInput,
  contextInput: CommandContext,
  deps: MeetingCommandDeps,
): MeetingCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof scheduleMeetingSchema>;
  try {
    draft = scheduleMeetingSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "meetings.schedule", fingerprint);
  if (replay) return replay;

  if (!state.leads.some((lead) => lead.id === draft.leadId))
    return fail(stateInput, context, "not_found", "Lead não encontrado.");
  if (
    draft.membershipId &&
    state.membershipIds &&
    !state.membershipIds.includes(draft.membershipId)
  )
    return fail(stateInput, context, "not_found", "Participação não encontrada.");

  const occurredAt = deps.now();
  const meeting: Meeting = {
    id: deps.uuid(),
    leadId: draft.leadId,
    membershipId: draft.membershipId,
    title: draft.title,
    startsAt: new Date(draft.startsAt).toISOString(),
    durationMinutes: draft.durationMinutes,
    participants: [...new Set(draft.participants)],
    notes: draft.notes,
    link: draft.link,
    createdAt: occurredAt,
    updatedAt: occurredAt,
    version: 1,
  };

  return finish({
    state,
    context,
    command: "meetings.schedule",
    fingerprint,
    value: { meeting },
    nextState: { ...state, meetings: [...state.meetings, meeting] },
    after: { ...meeting },
    deps,
  });
}

export function executeRescheduleMeeting(
  stateInput: MeetingCommandState,
  input: RescheduleMeetingInput,
  contextInput: CommandContext,
  deps: MeetingCommandDeps,
): MeetingCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof rescheduleMeetingSchema>;
  try {
    draft = rescheduleMeetingSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "meetings.reschedule", fingerprint);
  if (replay) return replay;

  const found = openMeeting(state, draft.meetingId);
  if ("error" in found) return refuseClosed(stateInput, context, found.error, "remarcada");
  const current = found.meeting;
  if (draft.expectedVersion !== undefined && current.version !== draft.expectedVersion)
    return fail(
      stateInput,
      context,
      "version_conflict",
      "A reunião mudou em outro lugar. Recarregue antes de salvar.",
    );

  const startsAt = new Date(draft.startsAt).toISOString();
  const durationMinutes = draft.durationMinutes ?? current.durationMinutes;
  if (startsAt === current.startsAt && durationMinutes === current.durationMinutes)
    return fail(stateInput, context, "invalid_state", "A reunião já está neste horário.");

  const updated: Meeting = {
    ...current,
    startsAt,
    durationMinutes,
    updatedAt: deps.now(),
    version: current.version + 1,
  };

  return finish({
    state,
    context,
    command: "meetings.reschedule",
    fingerprint,
    value: { meeting: updated },
    nextState: {
      ...state,
      meetings: state.meetings.map((item) => (item.id === updated.id ? updated : item)),
    },
    before: { startsAt: current.startsAt, durationMinutes: current.durationMinutes },
    after: { startsAt, durationMinutes },
    deps,
  });
}

export function executeUpdateMeeting(
  stateInput: MeetingCommandState,
  input: UpdateMeetingInput,
  contextInput: CommandContext,
  deps: MeetingCommandDeps,
): MeetingCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof updateMeetingSchema>;
  try {
    draft = updateMeetingSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "meetings.update", fingerprint);
  if (replay) return replay;

  const found = openMeeting(state, draft.meetingId);
  if ("error" in found) return refuseClosed(stateInput, context, found.error, "editada");
  const current = found.meeting;
  if (draft.expectedVersion !== undefined && current.version !== draft.expectedVersion)
    return fail(
      stateInput,
      context,
      "version_conflict",
      "A reunião mudou em outro lugar. Recarregue antes de salvar.",
    );

  const updated: Meeting = {
    ...current,
    title: draft.title ?? current.title,
    participants: draft.participants ? [...new Set(draft.participants)] : current.participants,
    notes: draft.notes ?? current.notes,
    link: draft.link ?? current.link,
    updatedAt: deps.now(),
    version: current.version + 1,
  };

  return finish({
    state,
    context,
    command: "meetings.update",
    fingerprint,
    value: { meeting: updated },
    nextState: {
      ...state,
      meetings: state.meetings.map((item) => (item.id === updated.id ? updated : item)),
    },
    before: { title: current.title, participants: current.participants },
    after: { title: updated.title, participants: updated.participants },
    deps,
  });
}

/**
 * Confirma que a reunião aconteceu.
 *
 * Este é o ponto em que uma reunião vira interação real: o evento nasce com
 * `countsAsInteraction: true` e o resultado devolve `interactionAt` para a
 * store gravar `lastInteractionAt` na mesma operação (§7.7). Confirmar uma
 * reunião que ainda não começou é recusado — não há o que confirmar.
 */
export function executeHoldMeeting(
  stateInput: MeetingCommandState,
  input: HoldMeetingInput,
  contextInput: CommandContext,
  deps: MeetingCommandDeps,
): MeetingCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof holdMeetingSchema>;
  try {
    draft = holdMeetingSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "meetings.hold", fingerprint);
  if (replay) return replay;

  const found = openMeeting(state, draft.meetingId);
  if ("error" in found) return refuseClosed(stateInput, context, found.error, "confirmada");
  const current = found.meeting;
  if (draft.expectedVersion !== undefined && current.version !== draft.expectedVersion)
    return fail(
      stateInput,
      context,
      "version_conflict",
      "A reunião mudou em outro lugar. Recarregue antes de confirmar.",
    );

  const occurredAt = deps.now();
  const heldAt = draft.heldAt ? new Date(draft.heldAt).toISOString() : occurredAt;
  if (heldAt > occurredAt)
    return fail(stateInput, context, "invalid_state", "Uma reunião futura ainda não aconteceu.");
  if (heldAt < current.startsAt)
    return fail(
      stateInput,
      context,
      "invalid_state",
      "A confirmação não pode ser anterior ao início marcado.",
    );

  const updated: Meeting = {
    ...current,
    heldAt,
    notes: draft.notes ?? current.notes,
    updatedAt: occurredAt,
    version: current.version + 1,
  };
  const event: ActivityEvent = {
    id: deps.uuid(),
    leadId: current.leadId,
    membershipId: current.membershipId,
    type: "meeting_completed",
    title: `Reunião realizada: ${current.title}`,
    description: updated.notes,
    occurredAt: heldAt,
    kind: "event",
    actorType: context.actorType,
    actorId: context.actorId,
    origin: context.origin,
    commandId: context.commandId,
    correlationId: context.correlationId,
    countsAsInteraction: true,
  };

  return finish({
    state,
    context,
    command: "meetings.hold",
    fingerprint,
    value: { meeting: updated, interactionAt: heldAt },
    nextState: {
      ...state,
      meetings: state.meetings.map((item) => (item.id === updated.id ? updated : item)),
      events: [...state.events, event],
      // A marca de interação acompanha o evento: não existe caminho em que um
      // seja gravado sem o outro.
      leads: state.leads.map((lead) =>
        lead.id === current.leadId
          ? {
              ...lead,
              // Confirmar uma reunião antiga não pode apagar um contato mais
              // recente: a marca avança, nunca recua.
              lastInteractionAt:
                lead.lastInteractionAt && lead.lastInteractionAt > heldAt
                  ? lead.lastInteractionAt
                  : heldAt,
            }
          : lead,
      ),
    },
    eventIds: [event.id],
    before: { heldAt: current.heldAt },
    after: { heldAt },
    deps,
  });
}

export function executeCancelMeeting(
  stateInput: MeetingCommandState,
  input: CancelMeetingInput,
  contextInput: CommandContext,
  deps: MeetingCommandDeps,
): MeetingCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof cancelMeetingSchema>;
  try {
    draft = cancelMeetingSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "meetings.cancel", fingerprint);
  if (replay) return replay;

  const found = openMeeting(state, draft.meetingId);
  if ("error" in found) return refuseClosed(stateInput, context, found.error, "cancelada");
  const current = found.meeting;
  if (draft.expectedVersion !== undefined && current.version !== draft.expectedVersion)
    return fail(
      stateInput,
      context,
      "version_conflict",
      "A reunião mudou em outro lugar. Recarregue antes de cancelar.",
    );

  const occurredAt = deps.now();
  const updated: Meeting = {
    ...current,
    cancelledAt: occurredAt,
    cancelReason: draft.reason,
    updatedAt: occurredAt,
    version: current.version + 1,
  };
  // Cancelamento entra na jornada porque explica um buraco na agenda, mas NÃO
  // conta como interação: desmarcar não é conversar (§7.7).
  const event: ActivityEvent = {
    id: deps.uuid(),
    leadId: current.leadId,
    membershipId: current.membershipId,
    type: "meeting_cancelled",
    title: `Reunião cancelada: ${current.title}`,
    description: draft.reason,
    occurredAt,
    kind: "event",
    actorType: context.actorType,
    actorId: context.actorId,
    origin: context.origin,
    commandId: context.commandId,
    correlationId: context.correlationId,
    countsAsInteraction: false,
  };

  return finish({
    state,
    context,
    command: "meetings.cancel",
    fingerprint,
    value: { meeting: updated },
    nextState: {
      ...state,
      meetings: state.meetings.map((item) => (item.id === updated.id ? updated : item)),
      events: [...state.events, event],
    },
    eventIds: [event.id],
    before: { cancelledAt: current.cancelledAt },
    after: { cancelledAt: occurredAt, reason: draft.reason },
    deps,
  });
}

export interface MeetingDemoAdapter {
  schedule(
    input: ScheduleMeetingInput,
    context: CommandContext,
  ): Promise<CommandResult<MeetingCommandValue>>;
  reschedule(
    input: RescheduleMeetingInput,
    context: CommandContext,
  ): Promise<CommandResult<MeetingCommandValue>>;
  update(
    input: UpdateMeetingInput,
    context: CommandContext,
  ): Promise<CommandResult<MeetingCommandValue>>;
  hold(
    input: HoldMeetingInput,
    context: CommandContext,
  ): Promise<CommandResult<MeetingCommandValue>>;
  cancel(
    input: CancelMeetingInput,
    context: CommandContext,
  ): Promise<CommandResult<MeetingCommandValue>>;
  getState(): MeetingCommandState;
  syncExternal(state: MeetingCommandState): void;
}

export function createMeetingDemoAdapter(
  initialState: MeetingCommandState,
  deps: MeetingCommandDeps,
  onCommit?: (state: MeetingCommandState) => void,
): MeetingDemoAdapter {
  let state = cloneState(initialState);
  let queue: Promise<unknown> = Promise.resolve();
  let busy = false;

  const enqueue = (
    operation: () => MeetingCommandExecution,
  ): Promise<CommandResult<MeetingCommandValue>> => {
    const run = async () => {
      busy = true;
      try {
        const execution = operation();
        if (execution.result.ok && execution.result.status === "applied") {
          state = execution.state;
          onCommit?.(state);
        }
        return execution.result;
      } finally {
        busy = false;
      }
    };
    if (!busy) {
      const started = run();
      queue = started.then(
        () => undefined,
        () => undefined,
      );
      return started;
    }
    const next = queue.then(run);
    queue = next.then(
      () => undefined,
      () => undefined,
    );
    return next as Promise<CommandResult<MeetingCommandValue>>;
  };

  return {
    schedule: (input, context) => enqueue(() => executeScheduleMeeting(state, input, context, deps)),
    reschedule: (input, context) =>
      enqueue(() => executeRescheduleMeeting(state, input, context, deps)),
    update: (input, context) => enqueue(() => executeUpdateMeeting(state, input, context, deps)),
    hold: (input, context) => enqueue(() => executeHoldMeeting(state, input, context, deps)),
    cancel: (input, context) => enqueue(() => executeCancelMeeting(state, input, context, deps)),
    getState: () => state,
    syncExternal: (next) => {
      if (!busy) state = cloneState(next);
    },
  };
}
